import { useEffect, useRef, useState } from 'react';
import { agentsApi, AGENTS_MODIFIES } from '../../services/agentsApi.js';
import { compteAgentsActifs, normaliserListeAgents } from './agentsModel.js';

// Relecture de la pastille du bouton « Agents » tant que la page est visible
export const RELECTURE_COMPTE_MS = 60 * 1000;

/**
 * Nombre d'agents actifs de l'utilisateur (les siens et ses abonnements), pastille du bouton
 * « Agents » de l'en-tête, comme les non-lues sur la cloche. Lu au démarrage, chaque minute
 * tant que la page est visible, au retour sur la page, à la fermeture du tiroir des agents et
 * après chaque écriture réussie (AGENTS_MODIFIES : activation d'une carte, pause, reprise,
 * suppression…). Sans contexte complet (actif faux) : 0. Une lecture en échec garde la
 * dernière valeur, sans message : le centre des agents dit lui-même ses erreurs.
 */
export function useCompteAgents(user, { actif = false, tiroir = null } = {}) {
  const [compte, setCompte] = useState(0);
  const lireRef = useRef(null);

  useEffect(() => {
    if (!actif) {
      setCompte(0);
      lireRef.current = null;
      return undefined;
    }
    let controleur = null;
    let seq = 0;
    const lire = () => {
      if (document.visibilityState === 'hidden') return;
      controleur?.abort();
      controleur = new AbortController();
      const lecture = ++seq;
      agentsApi.liste(user, { signal: controleur.signal }).then(
        (reponse) => { if (lecture === seq) setCompte(compteAgentsActifs(normaliserListeAgents(reponse))); },
        () => {},
      );
    };
    const auRetour = () => { if (document.visibilityState === 'visible') lire(); };
    lireRef.current = lire;
    lire();
    const timer = setInterval(lire, RELECTURE_COMPTE_MS);
    window.addEventListener(AGENTS_MODIFIES, lire);
    document.addEventListener('visibilitychange', auRetour);
    return () => {
      clearInterval(timer);
      controleur?.abort();
      window.removeEventListener(AGENTS_MODIFIES, lire);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [user, actif]);

  // Tiroir des agents refermé : le compte suit ce qui vient d'y être vu
  const tiroirAvant = useRef(tiroir);
  useEffect(() => {
    if (tiroirAvant.current === 'agents' && tiroir !== 'agents') lireRef.current?.();
    tiroirAvant.current = tiroir;
  }, [tiroir]);

  return compte;
}
